import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, Download, FileText, Plus, Save } from "lucide-react";
import { useProjectContext } from "../../../hooks/useProjectContext";
import { projectsAPI } from "../../../services/api";
import { weeklyReportsAPI } from "../../../services/weeklyReportsAPI";

const RESULT_OPTIONS = [
  ["accepted", "Accepted"],
  ["accepted_with_comments", "Accepted w/ Comments"],
  ["rework_required", "Rework Required"],
  ["ncr_issued", "NCR Issued"],
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const firstDayOfMonthISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const emptySummary = {
  total: 0,
  open: 0,
  pending: 0,
  closed: 0,
  reopened: 0,
  critical: 0,
  overdue: 0,
};

const emptyForm = {
  report_number: "",
  period_start: "",
  period_end: "",
  project_name: "",
  contractor: "",
  inspector: "",
  discipline: "Electrical",
  inspection_numbers: "",
  work_inspected: "",
  observations: "",
  result: "",
  inspector_signature_name: "",
  inspector_signature_date: "",
  qaqc_signature_name: "",
  qaqc_signature_date: "",
  status: "draft",
};

const toInputForm = (report = {}) => ({
  ...emptyForm,
  ...report,
  period_start: report.period_start || "",
  period_end: report.period_end || report.week_ending || "",
  inspector_signature_date: report.inspector_signature_date || "",
  qaqc_signature_date: report.qaqc_signature_date || "",
});

const cleanPayload = (form) => ({
  ...form,
  report_number: undefined,
  period_start: form.period_start || null,
  period_end: form.period_end || null,
  week_ending: undefined,
  inspector_signature_date: form.inspector_signature_date || null,
  qaqc_signature_date: form.qaqc_signature_date || null,
});

const formatName = (user) =>
  user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : "";

const formatDate = (value) => {
  if (!value) return "-";
  try {
    const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
  } catch {
    return value;
  }
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const formatApiDetail = (detail) => {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item?.msg) return item.msg;
        if (item?.detail) return formatApiDetail(item.detail);
        try {
          return JSON.stringify(item);
        } catch {
          return String(item);
        }
      })
      .filter(Boolean)
      .join(" ");
  }
  if (typeof detail === "object") {
    if (detail.msg) return detail.msg;
    if (detail.detail) return formatApiDetail(detail.detail);
    try {
      return JSON.stringify(detail);
    } catch {
      return String(detail);
    }
  }
  return String(detail);
};

const extractApiErrorMessage = async (err, fallback) => {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      return formatApiDetail(parsed?.detail) || fallback;
    } catch {
      return fallback;
    }
  }
  return formatApiDetail(data?.detail) || err?.message || fallback;
};

function MetricCard({ label, value, tone = "gray" }) {
  const toneClass = {
    orange: "border-orange-100 bg-orange-50 text-orange-700",
    red: "border-red-100 bg-red-50 text-red-700",
    amber: "border-amber-100 bg-amber-50 text-amber-700",
    emerald: "border-emerald-100 bg-emerald-50 text-emerald-700",
    gray: "border-gray-100 bg-gray-50 text-gray-700",
  }[tone];
  return (
    <div className={`rounded-xl border p-3 ${toneClass}`}>
      <div className="text-xs font-semibold uppercase tracking-wide opacity-80">
        {label}
      </div>
      <div className="mt-1 text-2xl font-black">{value ?? 0}</div>
    </div>
  );
}

const WeeklyReportsView = () => {
  const { activeProject, currentUser } = useProjectContext();
  const projectId = activeProject?.id;

  const [reports, setReports] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [issuesSnapshot, setIssuesSnapshot] = useState({
    summary: emptySummary,
    items: [],
    key_items: [],
  });
  const [inspectionSummaries, setInspectionSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);
  const [error, setError] = useState("");
  const [projectDetails, setProjectDetails] = useState(null);

  const contractorFromProject = useMemo(() => {
    return (
      projectDetails?.client_org ||
      projectDetails?.contractor ||
      projectDetails?.client ||
      activeProject?.client_org ||
      activeProject?.contractor ||
      activeProject?.client ||
      ""
    );
  }, [activeProject, projectDetails]);

  const loadProjectDetails = useCallback(async () => {
    if (!projectId) return null;
    try {
      const res = await projectsAPI.getOne(projectId);
      setProjectDetails(res.data || null);
      return res.data || null;
    } catch (err) {
      console.warn("Inspection report project prefill skipped", err);
      setProjectDetails(null);
      return null;
    }
  }, [projectId]);

  const refreshPeriodContext = useCallback(
    async (
      periodStart,
      periodEnd,
      { overwriteInspectionNumbers = true } = {},
    ) => {
      if (!projectId || !periodStart || !periodEnd) return null;
      if (periodEnd < periodStart) {
        setError("Date To must be greater than or equal to Date From.");
        return null;
      }
      setContextLoading(true);
      try {
        const res = await weeklyReportsAPI.periodContext(projectId, {
          period_start: periodStart,
          period_end: periodEnd,
        });
        const ctx = res.data || {};
        setIssuesSnapshot(
          ctx.issues_snapshot || {
            summary: emptySummary,
            items: [],
            key_items: [],
          },
        );
        setInspectionSummaries(
          Array.isArray(ctx.inspection_summaries)
            ? ctx.inspection_summaries
            : [],
        );
        if (overwriteInspectionNumbers) {
          setForm((prev) => ({
            ...prev,
            inspection_numbers: ctx.inspection_numbers || "",
          }));
        }
        return ctx;
      } catch (err) {
        console.warn("Inspection report period context skipped", err);
        setIssuesSnapshot({ summary: emptySummary, items: [], key_items: [] });
        setInspectionSummaries([]);
        return null;
      } finally {
        setContextLoading(false);
      }
    },
    [projectId],
  );

  const buildPrefillForm = useCallback(
    async (base = {}) => {
      const details =
        projectDetails || (await loadProjectDetails()) || activeProject || {};
      const periodStart = base.period_start || firstDayOfMonthISO();
      const periodEnd = base.period_end || todayISO();
      const inspectorName = formatName(currentUser);
      const ctx = await refreshPeriodContext(periodStart, periodEnd, {
        overwriteInspectionNumbers: false,
      });
      return {
        ...emptyForm,
        period_start: periodStart,
        period_end: periodEnd,
        project_name:
          base.project_name || details.name || activeProject?.name || "",
        contractor:
          base.contractor ||
          details.client_org ||
          details.contractor ||
          details.client ||
          contractorFromProject,
        inspector: base.inspector || inspectorName,
        discipline: base.discipline || "Electrical",
        inspector_signature_name:
          base.inspector_signature_name || inspectorName,
        inspection_numbers:
          base.inspection_numbers || ctx?.inspection_numbers || "",
        ...base,
      };
    },
    [
      activeProject,
      contractorFromProject,
      currentUser,
      loadProjectDetails,
      projectDetails,
      refreshPeriodContext,
    ],
  );

  const refreshReportList = useCallback(async () => {
    if (!projectId) return [];
    const res = await weeklyReportsAPI.list(projectId);
    const rows = Array.isArray(res.data) ? res.data : [];
    setReports(rows);
    return rows;
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    const initialLoad = async () => {
      setLoading(true);
      setError("");
      try {
        const details = await loadProjectDetails();
        const res = await weeklyReportsAPI.list(projectId);
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        setReports(rows);
        if (rows.length) {
          const first = rows[0];
          setSelectedId(first.id);
          setForm(toInputForm(first));
          setIssuesSnapshot(
            first.issues_snapshot || {
              summary: emptySummary,
              items: [],
              key_items: [],
            },
          );
          if (first.period_start && (first.period_end || first.week_ending)) {
            await refreshPeriodContext(
              first.period_start,
              first.period_end || first.week_ending,
              { overwriteInspectionNumbers: false },
            );
          }
          return;
        }
        const inspectorName = formatName(currentUser);
        const periodStart = firstDayOfMonthISO();
        const periodEnd = todayISO();
        const ctx = await refreshPeriodContext(periodStart, periodEnd, {
          overwriteInspectionNumbers: false,
        });
        if (cancelled) return;
        setSelectedId(null);
        setForm({
          ...emptyForm,
          period_start: periodStart,
          period_end: periodEnd,
          project_name: details?.name || activeProject?.name || "",
          contractor:
            details?.client_org ||
            details?.contractor ||
            details?.client ||
            activeProject?.client_org ||
            activeProject?.contractor ||
            activeProject?.client ||
            "",
          inspector: inspectorName,
          discipline: "Electrical",
          inspector_signature_name: inspectorName,
          inspection_numbers: ctx?.inspection_numbers || "",
        });
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load inspection reports", err);
          setError("Failed to load inspection reports.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    initialLoad();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const validatePeriod = () => {
    if (!form.period_start) return "Date From is required.";
    if (!form.period_end) return "Date To is required.";
    if (form.period_end < form.period_start)
      return "Date To must be greater than or equal to Date From.";
    return "";
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError("");
    if (field === "period_start" || field === "period_end") {
      const next = { ...form, [field]: value };
      if (
        next.period_start &&
        next.period_end &&
        next.period_end >= next.period_start
      ) {
        refreshPeriodContext(next.period_start, next.period_end);
      }
    }
  };

  const handleNew = async () => {
    setCreating(true);
    setError("");
    try {
      setSelectedId(null);
      setForm(await buildPrefillForm());
    } catch (err) {
      setError(
        await extractApiErrorMessage(
          err,
          "Failed to start a new inspection report.",
        ),
      );
    } finally {
      setCreating(false);
    }
  };

  const handleSelect = (report) => {
    setSelectedId(report.id);
    setForm(toInputForm(report));
    setIssuesSnapshot(
      report.issues_snapshot || {
        summary: emptySummary,
        items: [],
        key_items: [],
      },
    );
    if (report.period_start && (report.period_end || report.week_ending)) {
      refreshPeriodContext(
        report.period_start,
        report.period_end || report.week_ending,
        { overwriteInspectionNumbers: false },
      );
    } else {
      setInspectionSummaries([]);
    }
    setError("");
  };

  const handleSave = async () => {
    const validationError = validatePeriod();
    if (validationError) {
      setError(validationError);
      return null;
    }
    setSaving(true);
    setError("");
    try {
      const payload = cleanPayload(form);
      const res = selectedId
        ? await weeklyReportsAPI.update(projectId, selectedId, payload)
        : await weeklyReportsAPI.create(projectId, payload);
      const saved = res.data;
      setSelectedId(saved.id);
      setForm(toInputForm(saved));
      setIssuesSnapshot(saved.issues_snapshot || issuesSnapshot);
      await refreshReportList();
      return saved;
    } catch (err) {
      setError(
        await extractApiErrorMessage(err, "Failed to save inspection report."),
      );
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    const validationError = validatePeriod();
    if (validationError) {
      setError(validationError);
      return;
    }
    setDownloading(true);
    setError("");
    try {
      let report = selectedId
        ? { id: selectedId, report_number: form.report_number }
        : null;
      if (!report?.id) report = await handleSave();
      if (!report?.id) return;
      const res = await weeklyReportsAPI.downloadPdf(projectId, report.id);
      const number =
        form.report_number ||
        report.report_number ||
        `inspection_report_${report.id}`;
      downloadBlob(res.data, `${number}.pdf`);
    } catch (err) {
      setError(
        await extractApiErrorMessage(
          err,
          "Failed to download PDF. Save the report first and try again.",
        ),
      );
    } finally {
      setDownloading(false);
    }
  };

  if (!activeProject) return null;

  const summary = issuesSnapshot?.summary || emptySummary;
  const issueItems = Array.isArray(issuesSnapshot?.items)
    ? issuesSnapshot.items
    : [];
  const keyItems = Array.isArray(issuesSnapshot?.key_items)
    ? issuesSnapshot.key_items
    : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <FileText className="h-7 w-7 text-orange-500" />
            Generate Inspection Reports
          </h2>
          <p className="text-sm text-gray-600">
            Create compact inspection reports for any selected reporting period.
          </p>
        </div>
        <button
          type="button"
          onClick={handleNew}
          disabled={creating || loading}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-600 disabled:opacity-60"
        >
          <Plus className="h-4 w-4" />
          Generate Inspection Report
        </button>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[320px_1fr]">
        <aside className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <div className="font-semibold text-gray-900">Saved Reports</div>
            {loading ? (
              <span className="text-xs text-gray-400">Loading…</span>
            ) : null}
          </div>
          <div className="max-h-[72vh] space-y-2 overflow-y-auto pr-1">
            {reports.length === 0 && !loading ? (
              <div className="rounded-xl border border-dashed p-4 text-sm text-gray-500">
                No inspection reports yet.
              </div>
            ) : null}
            {reports.map((report) => (
              <button
                key={report.id}
                type="button"
                onClick={() => handleSelect(report)}
                className={`w-full rounded-xl border p-3 text-left transition hover:border-orange-200 hover:bg-orange-50/50 ${
                  selectedId === report.id
                    ? "border-orange-300 bg-orange-50"
                    : "border-gray-200 bg-white"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-gray-900">
                      {report.report_number}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {formatDate(report.period_start)} –{" "}
                      {formatDate(report.period_end || report.week_ending)}
                    </div>
                    <div className="mt-1 line-clamp-1 text-xs text-gray-500">
                      {report.project_name || activeProject.name}
                    </div>
                  </div>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">
                    {report.status || "draft"}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </aside>

        <main className="space-y-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm md:p-6">
          <div className="flex flex-col gap-3 border-b border-gray-100 pb-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-orange-600">
                Reporting Period
              </div>
              <div className="mt-1 text-lg font-bold text-gray-900">
                {form.report_number || "New Inspection Report"}
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || loading}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                <Save className="h-4 w-4" />
                {saving ? "Saving…" : "Save Report"}
              </button>
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading || saving || loading}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-60"
              >
                <Download className="h-4 w-4" />
                {downloading ? "Preparing…" : "Download PDF"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <label className="text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                Date From *
              </span>
              <input
                type="date"
                value={form.period_start}
                onChange={(e) => updateField("period_start", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                Date To *
              </span>
              <input
                type="date"
                value={form.period_end}
                onChange={(e) => updateField("period_end", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                Project
              </span>
              <input
                value={form.project_name}
                onChange={(e) => updateField("project_name", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                Contractor
              </span>
              <input
                value={form.contractor}
                onChange={(e) => updateField("contractor", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                Inspector
              </span>
              <input
                value={form.inspector}
                onChange={(e) => updateField("inspector", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                Discipline
              </span>
              <input
                value={form.discipline}
                onChange={(e) => updateField("discipline", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </label>
          </div>

          <div className="rounded-xl border border-orange-100 bg-orange-50 p-3 text-sm text-orange-800">
            <div className="flex items-center gap-2 font-semibold">
              <Calendar className="h-4 w-4" />
              Inspection No(s) and issues are pulled from inspections within the
              selected reporting period.
            </div>
            {contextLoading ? (
              <div className="mt-1 text-xs">Refreshing period data…</div>
            ) : null}
          </div>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              Inspection No(s)
            </span>
            <textarea
              rows={2}
              value={form.inspection_numbers}
              onChange={(e) =>
                updateField("inspection_numbers", e.target.value)
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Auto-filled from inspections in the selected period."
            />
          </label>

          {inspectionSummaries.length > 0 ? (
            <div
              className="flex flex-wrap gap-2"
              aria-label="Inspection details for the selected period"
            >
              {inspectionSummaries.map((inspection) => (
                <span
                  key={inspection.id}
                  className="inline-flex max-w-full items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-700"
                >
                  <strong className="mr-1 text-gray-900">
                    {inspection.inspection_number || "-"}
                  </strong>
                  <span className="truncate">
                    – {inspection.inspection_type || "Inspection type not set"}
                  </span>
                </span>
              ))}
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
            <MetricCard label="Total" value={summary.total} />
            <MetricCard label="Open" value={summary.open} tone="orange" />
            <MetricCard label="Pending" value={summary.pending} tone="amber" />
            <MetricCard label="Closed" value={summary.closed} tone="emerald" />
            <MetricCard
              label="Reopened"
              value={summary.reopened}
              tone="orange"
            />
            <MetricCard label="Critical" value={summary.critical} tone="red" />
            <MetricCard label="Overdue" value={summary.overdue} tone="red" />
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="p-3">Issues</th>
                  <th className="p-3">Inspection No.</th>
                  <th className="p-3">Inspection Type</th>
                  <th className="p-3">Location</th>
                  <th className="p-3">Severity</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Responsible</th>
                  <th className="p-3">Due</th>
                  <th className="p-3">Description</th>
                </tr>
              </thead>
              <tbody>
                {issueItems.map((item) => (
                  <tr key={item.finding_id} className="border-t align-top">
                    <td className="p-3 font-medium text-gray-900">
                      {item.finding_number || item.finding_id}
                    </td>
                    <td className="p-3 text-gray-700">
                      {item.inspection_number || "-"}
                    </td>
                    <td className="p-3 text-gray-700">
                      {item.inspection_type || "-"}
                    </td>
                    <td className="p-3 text-gray-700">
                      {item.location || "-"}
                    </td>
                    <td className="p-3 text-gray-700">
                      {item.severity || "-"}
                    </td>
                    <td className="p-3 text-gray-700">{item.status || "-"}</td>
                    <td className="p-3 text-gray-700">
                      {item.responsible_user_name || "-"}
                    </td>
                    <td className="p-3 text-gray-700">
                      {item.due_date || "-"}
                    </td>
                    <td className="p-3 text-gray-700">
                      {item.description || item.description_short || "-"}
                    </td>
                  </tr>
                ))}
                {issueItems.length === 0 ? (
                  <tr className="border-t">
                    <td colSpan="9" className="p-4 text-center text-gray-500">
                      No issues raised during this reporting period.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                Work Inspected
              </span>
              <textarea
                rows={7}
                value={form.work_inspected}
                onChange={(e) => updateField("work_inspected", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                Observations / Non-Conformances
              </span>
              <textarea
                rows={7}
                value={form.observations}
                onChange={(e) => updateField("observations", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </label>
          </div>

          {/* {keyItems.length ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <div className="mb-2 font-semibold">
                Key Issues / Follow-up Notes
              </div>
              <ul className="list-disc space-y-1 pl-5">
                {keyItems.map((item, index) => (
                  <li key={`${item.finding_number}-${index}`}>
                    <span className="font-semibold">{item.finding_number}</span>
                    : {item.note}
                  </li>
                ))}
              </ul>
            </div>
          ) : null} */}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            <label className="text-sm xl:col-span-1">
              <span className="mb-1 block font-medium text-gray-700">
                Result
              </span>
              <select
                value={form.result}
                onChange={(e) => updateField("result", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Select result</option>
                {RESULT_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                Inspector Signature
              </span>
              <input
                value={form.inspector_signature_name}
                onChange={(e) =>
                  updateField("inspector_signature_name", e.target.value)
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                Inspector Date
              </span>
              <input
                type="date"
                value={form.inspector_signature_date}
                onChange={(e) =>
                  updateField("inspector_signature_date", e.target.value)
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                QA/QC Signature
              </span>
              <input
                value={form.qaqc_signature_name}
                onChange={(e) =>
                  updateField("qaqc_signature_name", e.target.value)
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                QA/QC Date
              </span>
              <input
                type="date"
                value={form.qaqc_signature_date}
                onChange={(e) =>
                  updateField("qaqc_signature_date", e.target.value)
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </label>
          </div>
        </main>
      </div>
    </div>
  );
};

export default WeeklyReportsView;
